import { useCallback, useState, useEffect } from "react";
import { useSnackbar } from "$utils/useSnackbar";
import useAuth from "./useAuth";
import { usePackageTypesQuery } from "./usePackageTypesQuery";
import {
  snackbarSuccessOptions,
  snackbarErrorOptions,
  MESSAGE_DELETE_OK,
  MESSAGE_DELETE_FAILED,
} from "$utils/SnackbarOptions";
import { ParcelService } from "$services";

const useParcel = (
  opId: string,
  initialParcel: any = null,
  onDeleteSuccess?: () => void,
  onDeleteComplete?: () => void
) => {
  const { data: packageTypes = [] } = usePackageTypesQuery();
  const { token } = useAuth();
  const [parcel, setParcel] = useState(initialParcel);

  useEffect(() => {
    const fetchExistingParcel = async () => {
      if (opId && !initialParcel) {
        const parcelData = await ParcelService.getParcelByOpId(opId);
        if (parcelData.error) {
          setParcel(null);
        } else {
          setParcel(parcelData);
        }
      }
    };

    fetchExistingParcel();
  }, [initialParcel, opId]);

  // Update
  const updateParcel = useCallback(
    async (opId: string, parcelObj: any) => {
      const result = await ParcelService.updateParcel(token, opId, parcelObj);
      setParcel(result);
    },
    [token]
  );

  // Create
  const createParcel = useCallback(
    async (parcelObj: any) => {
      const result = await ParcelService.createParcel(token, parcelObj);
      setParcel(result);
    },
    [token]
  );

  const [deleteSnack, setDeleteSnack] = useState(true);
  const [openSnackbar] = useSnackbar(
    deleteSnack ? snackbarSuccessOptions : snackbarErrorOptions
  );

  const deleteParcel = useCallback(
    async (opId: string) => {
      try {
        await ParcelService.deleteParcel(token, opId);
        setParcel(null);
        setDeleteSnack(true);
        openSnackbar(MESSAGE_DELETE_OK);
        // Close the modal after successful deletion
        if (onDeleteSuccess) {
          onDeleteSuccess();
        }
        // Close the main popin after successful deletion
        if (onDeleteComplete) {
          onDeleteComplete();
        }
      } catch (error: any) {
        setDeleteSnack(false);
        openSnackbar(MESSAGE_DELETE_FAILED, error.message);
      }
    },
    [openSnackbar, token, onDeleteSuccess, onDeleteComplete]
  );

  const packagesWithTypes = parcel?.packages?.map((packageItem) => ({
    ...packageItem,
    type: packageTypes.find(
      (packageType) => packageType.id === packageItem.typeId
    ),
  }));

  return {
    parcel: packagesWithTypes
      ? { ...parcel, packages: packagesWithTypes }
      : parcel,
    createParcel: (newParcel) => createParcel({ ...newParcel, opId }),
    updateParcel: (parcelObj) => updateParcel(opId, parcelObj),
    deleteParcel: () => deleteParcel(opId),
    packageTypes,
  };
};

export default useParcel;
